// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BiMapAlt, BiSolidGridAlt, BiTable } from 'react-icons/bi';

// TYPES ---------------------------------------------------------------- //

/** The view modes this toggle offers; the search-results toolbar is its consumer. */
type ViewMode = 'table' | 'tiles' | 'map';

interface ViewModeToggleProps {
  viewMode: ViewMode;
  onViewModeChange: (newViewMode: ViewMode) => void;
  showTilesOption?: boolean;
  showMapOption?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const ViewModeToggle = ({
  viewMode,
  onViewModeChange,
  showTilesOption = true,
  showMapOption = false,
}: ViewModeToggleProps) => {
  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newViewMode: ViewMode | null) => {
    if (newViewMode !== null) {
      onViewModeChange(newViewMode as ViewMode);
    }
  };

  // A toggle offering one view teaches nothing — with only the table left, render no toggle at all
  if (!showTilesOption && !showMapOption) return null;

  return (
    <ToggleButtonGroup aria-label='view mode' exclusive value={viewMode} onChange={handleViewModeChange}>
      <ToggleButton aria-label='table view' color='utility' value='table'>
        <BiTable style={{ marginRight: 8 }} />
        Table
      </ToggleButton>

      {showTilesOption ? (
        <ToggleButton aria-label='tile view' color='primary' value='tiles'>
          <BiSolidGridAlt style={{ marginRight: 8 }} />
          Tiles
        </ToggleButton>
      ) : null}

      {showMapOption ? (
        <ToggleButton aria-label='map view' color='primary' value='map'>
          <BiMapAlt style={{ marginRight: 8 }} />
          Map
        </ToggleButton>
      ) : null}
    </ToggleButtonGroup>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default ViewModeToggle;
